import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { notificationApi } from "@bw/shared/api";
import type { NotificationSend } from "@bw/shared/types/generated";
import { getUserDisplayName } from "@bw/shared/utils";

import { PlatformPage } from "@/components/PlatformPage";

import { banUser, getUser, sendPasswordReset, unbanUser } from "../api";
import { UserActivityPanel } from "../components/UserActivityPanel";
import { UserDetailHeader } from "../components/UserDetailHeader";
import { UserEligibilityCorrectionPanel } from "../components/UserEligibilityCorrectionPanel";
import {
  BanDialog,
  NotifyDialog,
  type NotifyChannel,
} from "../components/UserDialogs";
import { UserModerationPanel } from "../components/UserModerationPanel";
import { UserPlatformAdminPanel } from "../components/UserPlatformAdminPanel";
import { UserRolesPanel } from "../components/UserRolesPanel";

/** User detail query, preserved operator mutations, and MUI detail composition. */
export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [banExpiresAt, setBanExpiresAt] = useState("");
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [notifyTitle, setNotifyTitle] = useState("");
  const [notifyBody, setNotifyBody] = useState("");
  const [notifyChannel, setNotifyChannel] = useState<NotifyChannel>("in_app");
  const [notifyError, setNotifyError] = useState<string | null>(null);
  const [notifyFeedback, setNotifyFeedback] = useState<string | null>(null);
  const notifyCommandKey = useRef<string | null>(null);
  const [grantAdminFeedback, setGrantAdminFeedback] = useState<string | null>(
    null,
  );
  const handleGrantFeedbackChange = useCallback((feedback: string | null) => {
    setGrantAdminFeedback(feedback);
    if (feedback) setNotifyFeedback(null);
  }, []);

  const {
    data: user,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["platform", "user", id],
    queryFn: () => getUser(id!),
    enabled: !!id,
  });

  const banMutation = useMutation({
    mutationFn: () =>
      banUser(
        id!,
        banReason.trim(),
        banExpiresAt ? new Date(banExpiresAt).toISOString() : null,
      ),
    onSuccess: async () => {
      setShowBanDialog(false);
      setBanReason("");
      setBanExpiresAt("");
      await queryClient.invalidateQueries({
        queryKey: ["platform", "user", id],
      });
      await queryClient.invalidateQueries({ queryKey: ["platform", "users"] });
    },
  });

  const unbanMutation = useMutation({
    mutationFn: () => unbanUser(id!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["platform", "user", id],
      });
      await queryClient.invalidateQueries({ queryKey: ["platform", "users"] });
    },
  });

  const resetMutation = useMutation({
    mutationFn: (email: string) => sendPasswordReset(email),
  });

  const notifyMutation = useMutation({
    mutationFn: (payload: NotificationSend) => notificationApi.send(payload),
    onSuccess: (outcome) => {
      setShowNotifyDialog(false);
      setNotifyTitle("");
      setNotifyBody("");
      setNotifyChannel("in_app");
      setNotifyError(null);
      setGrantAdminFeedback(null);
      notifyCommandKey.current = null;
      const feedback = {
        persisted: "Notification added to the user's inbox.",
        queued: "Notification queued for delivery.",
        duplicate: "This notification was already submitted.",
        skipped_deleted:
          "Notification skipped because the account is unavailable.",
      }[outcome.disposition];
      setNotifyFeedback(feedback);
    },
    onError: (error) => {
      setNotifyError(
        error instanceof Error ? error.message : "Failed to send notification.",
      );
    },
  });

  if (isLoading) {
    return <PlatformPage title="User details" isLoading />;
  }

  if (isError || !user) {
    return (
      <PlatformPage title="User details">
        <Alert severity="error" role="alert">
          Failed to load user details.
        </Alert>
      </PlatformPage>
    );
  }

  const openNotifyDialog = () => {
    setNotifyError(null);
    setNotifyFeedback(null);
    setGrantAdminFeedback(null);
    notifyCommandKey.current = crypto.randomUUID();
    setShowNotifyDialog(true);
  };

  const closeNotifyDialog = () => {
    setShowNotifyDialog(false);
    setNotifyTitle("");
    setNotifyBody("");
    setNotifyChannel("in_app");
    setNotifyError(null);
    notifyCommandKey.current = null;
  };

  const handleNotifySend = () => {
    if (notifyMutation.isPending) return;
    const title = notifyTitle.trim();
    const body = notifyBody.trim();
    if (!title || !body) return;

    const idempotencyKey =
      notifyCommandKey.current ??
      (notifyCommandKey.current = crypto.randomUUID());
    let payload: NotificationSend;
    if (notifyChannel === "email") {
      if (!user.email) return;
      payload = {
        user_id: user.uid,
        title,
        body,
        channel: "email",
        notification_type: "general",
        idempotency_key: `platform-user-notification:${idempotencyKey}`,
      };
    } else {
      payload = {
        user_id: user.uid,
        title,
        body,
        channel: "in_app",
        notification_type: "general",
        idempotency_key: `platform-user-notification:${idempotencyKey}`,
      };
    }

    setNotifyError(null);
    notifyMutation.mutate(payload);
  };

  const activitySummary = [
    {
      label: "Recent audit entries",
      value: String(user.activity_summary.recent_audit_count),
    },
    {
      label: "Reports filed",
      value: String(user.activity_summary.reports_filed_count),
    },
    {
      label: "Reports against",
      value: String(user.activity_summary.reports_against_count),
    },
    {
      label: "Linked organizations",
      value: String(user.org_memberships.length),
    },
  ];
  const pageFeedback = grantAdminFeedback ?? notifyFeedback;

  return (
    <>
      <UserDetailHeader
        user={user}
        onNotify={openNotifyDialog}
        onSendReset={() => user.email && resetMutation.mutate(user.email)}
        onBan={() => setShowBanDialog(true)}
        onUnban={() => unbanMutation.mutate()}
      >
        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={
            notifyFeedback
              ? {
                  borderLeft: 3,
                  borderColor: "success.main",
                  px: 2,
                  py: 1,
                }
              : {
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  p: 0,
                  m: -1,
                  overflow: "hidden",
                  clip: "rect(0 0 0 0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }
          }
        >
          {pageFeedback ? (
            <Typography
              color="success.main"
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              {pageFeedback}
            </Typography>
          ) : null}
        </Box>

        {!showNotifyDialog && notifyError ? (
          <Alert severity="error" role="alert">
            {notifyError}
          </Alert>
        ) : null}

        <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
            Activity summary
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2,
              mt: 2,
            }}
          >
            {activitySummary.map((item) => (
              <Box key={item.label}>
                <Typography color="text.secondary" variant="caption">
                  {item.label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              xl: "minmax(0, 1.45fr) minmax(320px, 360px)",
            },
            gap: 3,
            alignItems: "start",
          }}
        >
          <Stack spacing={3}>
            <UserRolesPanel user={user} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  lg: "minmax(0, 1.05fr) minmax(0, 1fr)",
                },
                gap: 3,
                alignItems: "start",
              }}
            >
              <UserModerationPanel user={user} />
              <UserActivityPanel user={user} />
            </Box>
          </Stack>

          <Stack component="aside" spacing={3}>
            <UserPlatformAdminPanel
              user={user}
              onGrantFeedbackChange={handleGrantFeedbackChange}
            />
            <UserEligibilityCorrectionPanel userId={user.uid} />
          </Stack>
        </Box>
      </UserDetailHeader>

      <NotifyDialog
        open={showNotifyDialog}
        recipientName={getUserDisplayName(user.display_name, user.uid)}
        recipientEmail={user.email}
        title={notifyTitle}
        body={notifyBody}
        channel={notifyChannel}
        error={showNotifyDialog ? notifyError : null}
        saving={notifyMutation.isPending}
        onTitleChange={setNotifyTitle}
        onBodyChange={setNotifyBody}
        onChannelChange={setNotifyChannel}
        onClose={closeNotifyDialog}
        onSend={handleNotifySend}
      />

      <BanDialog
        open={showBanDialog}
        reason={banReason}
        expiresAt={banExpiresAt}
        saving={banMutation.isPending}
        onReasonChange={setBanReason}
        onExpiresChange={setBanExpiresAt}
        onClose={() => {
          setShowBanDialog(false);
          setBanReason("");
          setBanExpiresAt("");
        }}
        onConfirm={() => banMutation.mutate()}
      />
    </>
  );
}
